import { useState, useEffect, useCallback, useRef } from "react";

/**
 * A generic hook for managing search, filters, pagination, and loading state across multiple tabs.
 *
 * @param {Object} config
 * @param {Array} config.tabs - Array of tab definitions: [{ id, status, initialFilters }]
 * @param {any} config.initialTabValue - The ID of the initially active tab (default: 1)
 * @param {number} config.pageSize - Number of items per page (default: 10)
 * @param {Function} config.fetchDataFn - Async function to fetch data:
 *   ({ tabId, status, searchTerm, pageNum, pageSize, filters }) => Promise<{ items: Array, hasNext: boolean }>
 */
export const useTabbedSearch = ({
  tabs = [],
  initialTabValue = 1,
  pageSize = 10,
  fetchDataFn,
  cacheKey = null,
}) => {
  const [tabValue, setTabValue] = useState(initialTabValue);
  const [viewType, setViewType] = useState("grid");
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Consolidated state for all tabs
  const [tabStates, setTabStates] = useState(() => {
    let cached = null;
    if (cacheKey) {
      try {
        const stored = localStorage.getItem(`swr_cache_${cacheKey}`);
        if (stored) {
          cached = JSON.parse(stored);
        }
      } catch (e) {
        console.error("Failed to parse cache:", e);
      }
    }

    const initial = {};
    tabs.forEach((tab) => {
      const cachedTab = cached?.[tab.id] || {};
      initial[tab.id] = {
        items: cachedTab.items || [],
        loading: tab.id === initialTabValue,
        error: null,
        searchTerm: cachedTab.searchTerm || "",
        currentPage: cachedTab.currentPage || 0,
        hasMore: cachedTab.hasMore !== undefined ? cachedTab.hasMore : true,
        filters: cachedTab.filters || tab.initialFilters || {},
        fetched: false, // Track if initial load has run
      };
    });
    return initial;
  });

  // Keep a mutable ref of states to prevent rebuilding callbacks on every state change
  const tabStatesRef = useRef();
  tabStatesRef.current = tabStates;

  // Track which tabs have had their initial fetch initiated
  const initiatedTabs = useRef(new Set());

  // The primary, generic search execution handler
  const fetchTabContent = useCallback(
    async ({ tabId, term, pageNum, filters, reset = false }) => {
      const tabConfig = tabs.find((t) => t.id === tabId);
      if (!tabConfig) return;

      setTabStates((prev) => ({
        ...prev,
        [tabId]: {
          ...prev[tabId],
          loading: true,
          error: null,
        },
      }));

      try {
        const activeState = tabStatesRef.current[tabId] || {};
        const activeTerm =
          term !== undefined ? term : activeState.searchTerm || "";
        const activeFilters =
          filters !== undefined ? filters : activeState.filters || {};
        const activePageNum =
          pageNum !== undefined ? pageNum : activeState.currentPage || 0;

        // Invoke the delegated fetch function
        const result = await fetchDataFn({
          tabId,
          status: tabConfig.status,
          searchTerm: activeTerm,
          pageNum: activePageNum,
          pageSize,
          filters: activeFilters,
        });

        const newItems = result.items || [];
        const hasNext = result.hasNext || false;

        setTabStates((prev) => {
          const currentTabState = prev[tabId] || {};
          const nextState = {
            ...prev,
            [tabId]: {
              ...currentTabState,
              items: reset ? newItems : [...currentTabState.items, ...newItems],
              currentPage: reset ? 0 : activePageNum,
              hasMore: hasNext,
              searchTerm: activeTerm,
              filters: activeFilters,
              loading: false,
              error: null,
              fetched: true,
            },
          };

          if (cacheKey) {
            try {
              const cacheData = {};
              Object.keys(nextState).forEach((id) => {
                const itemsSlice = nextState[id].items.slice(0, pageSize);
                cacheData[id] = {
                  items: itemsSlice,
                  searchTerm: nextState[id].searchTerm,
                  currentPage: 0,
                  hasMore: nextState[id].hasMore,
                  filters: nextState[id].filters,
                };
              });
              localStorage.setItem(
                `swr_cache_${cacheKey}`,
                JSON.stringify(cacheData),
              );
            } catch (e) {
              console.error("Failed to save to SWR cache:", e);
            }
          }

          return nextState;
        });
      } catch (err) {
        console.error(`Error fetching tab ${tabId}:`, err);
        setTabStates((prev) => ({
          ...prev,
          [tabId]: {
            ...prev[tabId],
            error:
              err.message || "Failed to fetch content. Please try again later.",
            hasMore: false,
            loading: false,
            fetched: true,
          },
        }));
      }
    },
    [tabs, fetchDataFn, pageSize, cacheKey],
  );

  // Automatically trigger fetch when switching to a tab that hasn't been loaded yet
  useEffect(() => {
    const activeState = tabStates[tabValue];
    if (activeState && !activeState.fetched && !activeState.error) {
      if (!initiatedTabs.current.has(tabValue)) {
        initiatedTabs.current.add(tabValue);
        fetchTabContent({
          tabId: tabValue,
          term: activeState.searchTerm,
          pageNum: 0,
          filters: activeState.filters,
          reset: true,
        });
      }
    }
  }, [tabValue, tabStates, fetchTabContent]);

  // Load more handler for infinite scroll
  const loadMore = useCallback(() => {
    const activeState = tabStatesRef.current[tabValue];
    if (activeState && !activeState.loading && activeState.hasMore) {
      fetchTabContent({
        tabId: tabValue,
        pageNum: activeState.currentPage + 1,
        reset: false,
      });
    }
  }, [tabValue, fetchTabContent]);

  // Reset page, apply new search filters, and fetch
  const submitSearchParams = useCallback(
    (term, filters) => {
      const activeState = tabStatesRef.current[tabValue] || {};
      fetchTabContent({
        tabId: tabValue,
        term,
        filters: filters !== undefined ? filters : activeState.filters,
        pageNum: 0,
        reset: true,
      });
    },
    [tabValue, fetchTabContent],
  );

  const handleTabChange = useCallback((_event, newValue) => {
    setTabValue(newValue);
  }, []);

  const handleViewTypeChange = useCallback((type) => {
    setViewType(type);
  }, []);

  const getTabContents = useCallback(() => {
    return tabStates[tabValue]?.items || [];
  }, [tabValue, tabStates]);

  const refreshTab = useCallback(
    (tabId) => {
      const state = tabStatesRef.current[tabId] || {};
      return fetchTabContent({
        tabId,
        term: state.searchTerm || "",
        filters: state.filters || {},
        pageNum: 0,
        reset: true,
      });
    },
    [fetchTabContent],
  );

  const activeTabState = tabStates[tabValue] || {};

  return {
    loading: activeTabState.loading,
    error: activeTabState.error,
    searchTerm: activeTabState.searchTerm,
    filters: activeTabState.filters,
    hasMore: activeTabState.hasMore,
    viewType,
    tabValue,
    showCreateForm,
    setShowCreateForm,
    submitSearchParams,
    loadMore,
    handleTabChange,
    handleViewTypeChange,
    getTabContents,
    refreshTab,
    tabStates,
  };
};
